import { useEffect, useState } from 'react'

import {
  Button,
  DragDropFileList,
  Label,
  FileListFileProps,
} from '@mercai/clever'
import { useTranslation } from 'react-i18next'

import { getFileNameAndExtension } from '@/helpers'

import { api, FINANCE_PROFILE_URL } from '@/services'

import styles from './documents.module.css'

import type { ProfileCreditFormProps } from './documents.type'

import { FinanceProfileProps, FinanceProfilePersonTypeEnum } from '@/types'

export const ProfileCreditFormDocuments = ({
  onHandleOpen,
  profileUuid,
  onSuccessUpdated,
  financeProfileData,
}: ProfileCreditFormProps) => {
  const { t } = useTranslation('componentsFinanceProfileForm')

  const [values, setValues] = useState<Partial<FinanceProfileProps>>({})

  const [dataFilesGovernmentId, setDataFilesGovernmentId] = useState<
    FileListFileProps[]
  >([])
  const [dataFilesPowerOfAttorneyFile, setPowerOfAttorneyFile] = useState<
    FileListFileProps[]
  >([])
  const [dataFilesAddressProof, setDataFilesAddressProof] = useState<
    FileListFileProps[]
  >([])
  const [dataFilesConstitutiveAct, setDataFilesConstitutiveAct] = useState<
    FileListFileProps[]
  >([])
  const [dataRiskAssessmentReportFile, setRiskAssessmentReportFile] = useState<
    FileListFileProps[]
  >([])
  const [dataAnalysisReportFile, setAnalysisReportFile] = useState<
    FileListFileProps[]
  >([])
  const [dataSiteInspectionReport, setSiteInspectionReport] = useState<
    FileListFileProps[]
  >([])
  const [dataPromissoryNote, setPromissoryNote] = useState<FileListFileProps[]>(
    [],
  )

  useEffect(() => {
    if (financeProfileData) {
      setValues(financeProfileData)
    }

    if (financeProfileData?.governmentIdFile) {
      const file = getFileNameAndExtension(
        financeProfileData?.governmentIdFile || '',
      )

      setDataFilesGovernmentId([
        {
          id: file.fileName,
          name: `${file.fileName}.${file.extension}`,
          url: financeProfileData?.governmentIdFile,
          preview: financeProfileData?.governmentIdFile,
          uploaded: true,
        },
      ])
    }

    if (financeProfileData?.powerOfAttorneyFile) {
      const file = getFileNameAndExtension(
        financeProfileData?.powerOfAttorneyFile || '',
      )

      setPowerOfAttorneyFile([
        {
          id: file.fileName,
          name: `${file.fileName}.${file.extension}`,
          url: financeProfileData?.powerOfAttorneyFile,
          preview: financeProfileData?.powerOfAttorneyFile,
          uploaded: true,
        },
      ])
    }

    if (financeProfileData?.addressProofFile) {
      const file = getFileNameAndExtension(
        financeProfileData?.addressProofFile || '',
      )

      setDataFilesAddressProof([
        {
          id: file.fileName,
          name: `${file.fileName}.${file.extension}`,
          url: financeProfileData?.addressProofFile,
          preview: financeProfileData?.addressProofFile,
          uploaded: true,
        },
      ])
    }

    if (financeProfileData?.constitutiveActFile) {
      const file = getFileNameAndExtension(
        financeProfileData?.constitutiveActFile || '',
      )

      setDataFilesConstitutiveAct([
        {
          id: file.fileName,
          name: `${file.fileName}.${file.extension}`,
          url: financeProfileData?.constitutiveActFile,
          preview: financeProfileData?.constitutiveActFile,
          uploaded: true,
        },
      ])
    }

    if (financeProfileData?.riskAssessmentReportFile) {
      const file = getFileNameAndExtension(
        financeProfileData?.riskAssessmentReportFile || '',
      )

      setRiskAssessmentReportFile([
        {
          id: file.fileName,
          name: `${file.fileName}.${file.extension}`,
          url: financeProfileData?.riskAssessmentReportFile,
          preview: financeProfileData?.riskAssessmentReportFile,
          uploaded: true,
        },
      ])
    }

    if (financeProfileData?.analysisReportFile) {
      const file = getFileNameAndExtension(
        financeProfileData?.analysisReportFile || '',
      )

      setAnalysisReportFile([
        {
          id: file.fileName,
          name: `${file.fileName}.${file.extension}`,
          url: financeProfileData?.analysisReportFile,
          preview: financeProfileData?.analysisReportFile,
          uploaded: true,
        },
      ])
    }

    if (financeProfileData?.siteInspectionReportFile) {
      const file = getFileNameAndExtension(
        financeProfileData?.siteInspectionReportFile || '',
      )

      setSiteInspectionReport([
        {
          id: file.fileName,
          name: `${file.fileName}.${file.extension}`,
          url: financeProfileData?.siteInspectionReportFile,
          preview: financeProfileData?.siteInspectionReportFile,
          uploaded: true,
        },
      ])
    }

    if (financeProfileData?.promissoryNoteFile) {
      const file = getFileNameAndExtension(
        financeProfileData?.promissoryNoteFile || '',
      )

      setPromissoryNote([
        {
          id: file.fileName,
          name: `${file.fileName}.${file.extension}`,
          url: financeProfileData?.promissoryNoteFile,
          preview: financeProfileData?.promissoryNoteFile,
          uploaded: true,
        },
      ])
    }
  }, [financeProfileData])

  return (
    <div className={styles.core}>
      <div className={styles.content}>
        <div>
          <Label>
            {values?.personType === FinanceProfilePersonTypeEnum.individual
              ? t('profileCreditGovernmentIdLabel')
              : t('profileCreditLegalRepresentativeIdLabel')}
          </Label>

          <DragDropFileList
            translations={{
              clickToUpload: t('uploadButton'),
              dragFile: t('uploadDrag'),
              sizeFile: t('uploadSize'),
            }}
            urlAdd={FINANCE_PROFILE_URL(profileUuid)}
            configsRequest={{
              fileField: 'government_id_file',
              method: 'PATCH',
            }}
            data={dataFilesGovernmentId}
            onChangeData={setDataFilesGovernmentId}
            axiosInstance={api}
            successedUpload={onSuccessUpdated}
          />
        </div>

        <div>
          <Label>{t('profileCreditPowerOfAttorneyFileLabel')}</Label>
          <DragDropFileList
            translations={{
              clickToUpload: t('uploadButton'),
              dragFile: t('uploadDrag'),
              sizeFile: t('uploadSize'),
            }}
            urlAdd={FINANCE_PROFILE_URL(profileUuid)}
            configsRequest={{
              fileField: 'power_of_attorney_file',
              method: 'PATCH',
            }}
            data={dataFilesPowerOfAttorneyFile}
            onChangeData={setPowerOfAttorneyFile}
            axiosInstance={api}
            successedUpload={onSuccessUpdated}
          />
        </div>

        <div>
          <Label>{t('profileCreditAddressProofLabel')}</Label>
          <DragDropFileList
            translations={{
              clickToUpload: t('uploadButton'),
              dragFile: t('uploadDrag'),
              sizeFile: t('uploadSize'),
            }}
            urlAdd={FINANCE_PROFILE_URL(profileUuid)}
            configsRequest={{
              fileField: 'address_proof_file',
              method: 'PATCH',
            }}
            data={dataFilesAddressProof}
            onChangeData={setDataFilesAddressProof}
            axiosInstance={api}
            successedUpload={onSuccessUpdated}
          />
        </div>

        <div>
          <Label>{t('profileCreditRiskAssessmentReportFileLabel')}</Label>
          <DragDropFileList
            translations={{
              clickToUpload: t('uploadButton'),
              dragFile: t('uploadDrag'),
              sizeFile: t('uploadSize'),
            }}
            urlAdd={FINANCE_PROFILE_URL(profileUuid)}
            configsRequest={{
              fileField: 'risk_assessment_report_file',
              method: 'PATCH',
            }}
            data={dataRiskAssessmentReportFile}
            onChangeData={setRiskAssessmentReportFile}
            axiosInstance={api}
            successedUpload={onSuccessUpdated}
          />
        </div>

        <div>
          <Label>{t('profileCreditAnalysisReportFileLabel')}</Label>
          <DragDropFileList
            translations={{
              clickToUpload: t('uploadButton'),
              dragFile: t('uploadDrag'),
              sizeFile: t('uploadSize'),
            }}
            urlAdd={FINANCE_PROFILE_URL(profileUuid)}
            configsRequest={{
              fileField: 'analysis_report_file',
              method: 'PATCH',
            }}
            data={dataAnalysisReportFile}
            onChangeData={setAnalysisReportFile}
            axiosInstance={api}
            successedUpload={onSuccessUpdated}
          />
        </div>

        <div>
          <Label>{t('profileCreditSiteInspectionReportFileLabel')}</Label>
          <DragDropFileList
            translations={{
              clickToUpload: t('uploadButton'),
              dragFile: t('uploadDrag'),
              sizeFile: t('uploadSize'),
            }}
            urlAdd={FINANCE_PROFILE_URL(profileUuid)}
            configsRequest={{
              fileField: 'site_inspection_report_file',
              method: 'PATCH',
            }}
            data={dataSiteInspectionReport}
            onChangeData={setSiteInspectionReport}
            axiosInstance={api}
            successedUpload={onSuccessUpdated}
          />
        </div>

        <div>
          <Label>{t('profileCreditPromissoryNoteFileLabel')}</Label>
          <DragDropFileList
            translations={{
              clickToUpload: t('uploadButton'),
              dragFile: t('uploadDrag'),
              sizeFile: t('uploadSize'),
            }}
            urlAdd={FINANCE_PROFILE_URL(profileUuid)}
            configsRequest={{
              fileField: 'promissory_note_file',
              method: 'PATCH',
            }}
            data={dataPromissoryNote}
            onChangeData={setPromissoryNote}
            axiosInstance={api}
            successedUpload={onSuccessUpdated}
          />
        </div>

        {values?.personType === FinanceProfilePersonTypeEnum.legal && (
          <div>
            <Label>{t('profileCreditConstitutiveActLabel')}</Label>
            <DragDropFileList
              translations={{
                clickToUpload: t('uploadButton'),
                dragFile: t('uploadDrag'),
                sizeFile: t('uploadSize'),
              }}
              urlAdd={FINANCE_PROFILE_URL(profileUuid)}
              configsRequest={{
                fileField: 'constitutive_act_file',
                method: 'PATCH',
              }}
              data={dataFilesConstitutiveAct}
              onChangeData={setDataFilesConstitutiveAct}
              axiosInstance={api}
              successedUpload={onSuccessUpdated}
            />
          </div>
        )}
      </div>

      <div className={styles.footer}>
        <Button variant="secondary" onClick={() => onHandleOpen(false)}>
          {t('profileCreditButtonClose')}
        </Button>
        <Button onClick={() => onHandleOpen(false)}>
          {t('profileCreditButtonSubmit')}
        </Button>
      </div>
    </div>
  )
}
